import type { SeriesType } from './types';

/**
 * 시리즈 마크 — 플롯이 그리는 모양을 축약한 아이콘. 범례(`ChartLegendHtml`)와 툴팁(`CartesianTooltip`) 이
 * **같은 규칙**을 써야 두 곳의 아이콘이 서로, 또 플롯과 어긋나지 않으므로 여기로 모았다(SSOT).
 *
 * - `bar`/`area` = 채운 네모(면)
 * - `line` = 가로 선 + 중앙 점(`—•—`). 점 유무는 플롯의 dot 조건(`showDots ?? true`, chart-series 참조)을
 *   그대로 받는다 — 점을 끈 다채널 라인(스트링 12~24 등)은 아이콘에도 점을 안 찍어야 실제 마크와 맞는다.
 *
 * `hollow`(범례 off 상태) 표현은 모양별로 갈린다: 네모는 채움 대신 테두리로 비울 수 있지만, 선은 두께가 2px 라
 * 테두리를 두르면 형태가 뭉개져 알아볼 수 없으므로 색을 흐리게(opacity) 처리한다.
 */
// 크기 2종 — 범례(md)는 차트 밖 독립 블록, 툴팁(sm)은 좁은 행에 값까지 얹히므로 기존 밀도를 유지한다.
const SIZE = {
  sm: { square: 'size-2', line: 'w-3' },
  md: { square: 'size-2.5', line: 'w-3.5' },
} as const;

export function SeriesMark({
  type,
  color,
  showDots = true,
  hollow = false,
  size = 'md',
}: {
  type: SeriesType;
  /** CSS 색 — 범례는 시리즈 색, 툴팁은 `var(--color-{key})`. */
  color: string;
  /** `line` 전용 — 중앙 점 표시(기본 표시, 플롯 dot 기본값과 동일). */
  showDots?: boolean;
  /** 비활성(범례 토글 off) — 네모는 테두리만, 선은 흐리게. */
  hollow?: boolean;
  /** 'md'=범례(기본) · 'sm'=툴팁 행. */
  size?: keyof typeof SIZE;
}) {
  if (type === 'line') {
    return (
      <span
        className={`relative flex h-2 shrink-0 items-center justify-center ${SIZE[size].line}${
          hollow ? ' opacity-40' : ''
        }`}
      >
        <span className="h-0.5 w-full rounded-full" style={{ backgroundColor: color }} />
        {showDots && (
          <span className="absolute size-1.5 rounded-full" style={{ backgroundColor: color }} />
        )}
      </span>
    );
  }
  return (
    <span
      className={`shrink-0 rounded-[2px] ${SIZE[size].square}${hollow ? ' border' : ''}`}
      style={hollow ? { borderColor: color } : { backgroundColor: color }}
    />
  );
}
